/**
 * Landing page — what a logged-out visitor sees at playrexmortis.com.
 *
 * Before this existed, the bare login card greeted every first-time visitor:
 * no pitch, no art, no reason to register. Every marketing click bounced off
 * a password field. This page is the front gate: the wordmark, the hero, the
 * 24-second pitch, three vignettes, and one big door into the realm.
 *
 * Self-contained styles (injected once) so it can't disturb the app pages or
 * the theme-contrast guard. Uses design tokens with safe fallbacks.
 */

export function renderLanding(root: HTMLElement): void {
  if (!document.getElementById('landing-styles')) {
    const style = document.createElement('style');
    style.id = 'landing-styles';
    style.textContent = LANDING_STYLES;
    document.head.append(style);
  }

  const page = document.createElement('div');
  page.className = 'landing';
  page.innerHTML = `
    <header class="landing-hero">
      <img class="landing-hero-art" src="/img/hero.jpg" alt="" onerror="this.remove()" />
      <div class="landing-hero-veil"></div>
      <div class="landing-hero-inner">
        <img class="landing-logo" src="/img/ui/title.webp" alt="Rex Mortis" />
        <p class="landing-tagline">— Of crowns, bones, and the patient dead —</p>
        <p class="landing-pitch">
          A dark-medieval realm that lives in your browser. One character, one name,
          and a kingdom that remembers both. Train your arm, work the crooked road or
          the honest path, sail for foreign silks, and stand with the realm when the
          Crypt opens. No downloads. No item, stat, or level can be bought with real
          money — power here is earned, never sold. The patient prosper.
        </p>
        <div class="landing-actions">
          <a class="landing-btn landing-btn-primary" href="#/register">Forge a character</a>
          <a class="landing-btn" href="#/login">Sign in</a>
        </div>
      </div>
    </header>

    <section class="landing-vignettes">
      <div class="landing-vig">
        <div class="landing-vig-orn">☠</div>
        <h3>Crime, or virtue</h3>
        <p>
          Beg in the square or sack a free city. Raise a barn or raise the dead's
          due. Crime pays double — but the crooked road ends in the Dungeon, and
          even mercy can leave you bleeding. The realm only asks what kind of
          soul you are.
        </p>
      </div>
      <div class="landing-vig">
        <div class="landing-vig-orn">⚓︎</div>
        <h3>Coin on every road</h3>
        <p>
          Train, spar, fish the black lochs, mine the Iron Pits, and haul foreign
          goods home to the Merchant's Market. Bank what you keep — wallet crowns
          can be plundered, and the realm is not gentle.
        </p>
      </div>
      <div class="landing-vig">
        <div class="landing-vig-orn">☽</div>
        <h3>Stand together</h3>
        <p>
          Join a House, build its chain of victories, and fell the horror that
          rises in the Crypt each week — every blow counts, even a newcomer's.
          Glory draws the dark, and the Shade keeps its own ledger.
        </p>
      </div>
    </section>

    <section class="landing-close">
      <p class="landing-close-line">The gates are open. The realm will remember your name.</p>
      <a class="landing-btn landing-btn-primary" href="#/register">Forge a character</a>
    </section>

    <footer class="landing-foot">
      <a href="#/rules">Rules of the Realm</a>
      <span aria-hidden="true">·</span>
      <a href="#/tos">Terms of Service</a>
      <span aria-hidden="true">·</span>
      <a href="#/privacy">Privacy</a>
    </footer>
  `;
  root.append(page);
}

const LANDING_STYLES = `
  .landing {
    min-height: 100vh;
    background: var(--c-parchment, #f3ebd9);
    color: var(--c-ink, #1c1108);
    font-family: var(--font-body, Georgia, serif);
    display: flex;
    flex-direction: column;
    overflow-x: clip;
  }
  .landing > * {
    /* Flex items refuse to shrink below their content's min width by default
       (min-width: auto), which let the vignette grid push past a phone
       viewport and clip. Zeroing it keeps every section inside the screen. */
    min-width: 0;
  }
  .landing-hero {
    position: relative;
    overflow: hidden;
    text-align: center;
    padding: clamp(40px, 8vh, 96px) 20px 56px 20px;
    /* Matches the hero art's shadows — the art fades into this on wide screens. */
    background: #18100a;
  }
  .landing-hero-art {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    /* The source art is 1200px wide — capping the render keeps it crisp on
       large and ultrawide monitors instead of stretching to a blur. Edges
       fade into the dark backdrop above. */
    max-width: 1400px;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 14%, #000 86%, transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 14%, #000 86%, transparent 100%);
  }
  .landing-hero-veil {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(28, 17, 8, 0.55) 0%,
      rgba(28, 17, 8, 0.72) 60%,
      rgba(28, 17, 8, 0.92) 100%
    );
  }
  .landing-hero-inner {
    position: relative;
    max-width: 660px;
    margin: 0 auto;
  }
  .landing-logo {
    width: min(360px, 78vw);
    height: auto;
    filter: drop-shadow(0 4px 18px rgba(0, 0, 0, 0.6));
  }
  .landing-tagline {
    font-family: var(--font-elegant, Georgia, serif);
    font-style: italic;
    font-size: 1.05rem;
    color: #e8d9b0;
    margin: 14px 0 18px 0;
  }
  .landing-pitch {
    font-size: 1.12rem;
    line-height: 1.65;
    color: #f5ecd6;
    margin: 0 auto 26px auto;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  }
  .landing-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }
  .landing-btn {
    display: inline-block;
    padding: 13px 30px;
    font-family: var(--font-heading, Georgia, serif);
    font-size: 0.95rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-decoration: none;
    border-radius: 5px;
    border: 1px solid #b09a6a;
    background: rgba(243, 235, 217, 0.92);
    color: #3a2a18;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }
  .landing-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(0,0,0,0.35); }
  .landing-btn-primary {
    background: #6b1f1f;
    border-color: #3a0e0e;
    color: #f5e9c8;
  }
  .landing-vignettes {
    display: grid;
    /* Explicit column counts per breakpoint — auto-fit inside a flex item
       computed its track count from content size and overflowed on phones. */
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
    padding: 48px 24px 8px 24px;
    box-sizing: border-box;
  }
  @media (max-width: 920px) {
    .landing-vignettes { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 640px) {
    .landing-vignettes { grid-template-columns: 1fr; padding: 36px 16px 4px 16px; }
  }
  .landing-vig {
    background: var(--c-paper, #fbf4e4);
    border: 1px solid var(--c-rule, #b09a6a);
    border-radius: 8px;
    padding: 22px 20px;
    text-align: center;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.07);
  }
  .landing-vig-orn {
    font-size: 1.9rem;
    color: var(--c-gold, #b08d2f);
    line-height: 1;
    margin-bottom: 10px;
  }
  .landing-vig h3 {
    font-family: var(--font-heading, Georgia, serif);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-size: 1.02rem;
    color: var(--c-oxblood, #6b1f1f);
    margin: 0 0 8px 0;
  }
  .landing-vig p {
    margin: 0;
    line-height: 1.55;
    font-size: 0.98rem;
    text-align: left;
  }
  .landing-close {
    text-align: center;
    padding: 44px 20px 20px 20px;
  }
  .landing-close-line {
    font-family: var(--font-elegant, Georgia, serif);
    font-style: italic;
    font-size: 1.1rem;
    margin: 0 0 16px 0;
  }
  .landing-foot {
    text-align: center;
    padding: 26px 16px 30px 16px;
    font-size: 0.85rem;
    color: var(--c-ink-soft, #6b5a40);
    display: flex;
    gap: 10px;
    justify-content: center;
  }
  .landing-foot a { color: inherit; }
`;
